import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { ShieldCheck, BadgeCheck, AlertTriangle, UserRound, Lock } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [emp, setEmp] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrorMsg("");
    try {
      const u = await login(emp.trim(), pwd);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/mr", { replace: true });
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left visual */}
      <div className="hidden md:flex relative bg-brand-950 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background:radial-gradient(circle_at_30%_20%,#5a1519,transparent_60%),radial-gradient(circle_at_70%_80%,#3a0c0f,transparent_55%)]"></div>
        {/* Faint brand watermark, bottom-left like the client mock */}
        <img src="/logo-icon.png" alt="" aria-hidden="true" className="absolute -bottom-20 -left-20 w-96 opacity-[0.06] pointer-events-none select-none" />
        <div className="relative">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Zuventus Healthcare Limited" className="h-14 w-auto rounded-xl object-contain bg-white p-1.5" />
            <div>
              <div className="font-display font-bold tracking-tight text-lg">Zuventus Healthcare Limited</div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-300">Master of ICU</div>
            </div>
          </div>
        </div>

        <div className="relative w-full max-w-2xl mx-auto text-center space-y-6">
          <h1 className="font-display text-6xl xl:text-7xl font-extrabold tracking-tight leading-none uppercase">
            Master of ICU
          </h1>
          <div className="flex items-center justify-center gap-3" aria-hidden="true">
            <span className="h-px w-28 bg-gradient-to-r from-transparent to-amber-400/80"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            <span className="h-px w-28 bg-gradient-to-l from-transparent to-amber-400/80"></span>
          </div>
          <div className="font-display text-4xl xl:text-5xl font-bold text-brand-300">Certificate of Honour</div>
          <div className="flex items-center justify-center gap-3" aria-hidden="true">
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-amber-400/60"></span>
            <span className="text-amber-400/80 text-sm leading-none">❖</span>
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-amber-400/60"></span>
          </div>
          <p className="text-slate-200 text-lg leading-relaxed max-w-md mx-auto">
            Upload the doctor's photo, fill the details, and generate a personalized certificate.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <span className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/25 text-sm font-medium"><Lock className="w-4 h-4"/> Secure login</span>
            <span className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-white/25 text-sm font-medium"><BadgeCheck className="w-4 h-4"/> Brand consistent</span>
          </div>
        </div>

        <div className="relative text-xs text-slate-400">© 2026 Zuventus Healthcare Limited.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-7" data-testid="login-form">

          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3">
            <img src="/logo-icon.png" alt="Zuventus" className="h-10 w-auto rounded-lg object-contain" />
            <div className="font-display font-bold text-slate-900">Zuventus · Master of ICU</div>
          </div>

          {/* Desktop logo above form */}
          <div className="hidden md:flex justify-center">
            <img src="/logo.png" alt="Zuventus Healthcare Limited" className="h-20 w-auto object-contain" />
          </div>

          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Sign in</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Welcome back</h2>
            <p className="text-sm text-slate-500">Use your EMP Code and password to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emp" className="text-xs font-semibold uppercase tracking-wider text-slate-600">EMP Code</Label>
              <div className="relative">
                <UserRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  id="emp" data-testid="login-emp-input"
                  placeholder="Enter your EMP code"
                  value={emp} onChange={(e) => setEmp(e.target.value)}
                  required autoFocus
                  className="h-12 pl-10 bg-white border-slate-200 focus-visible:ring-brand-800"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  id="pwd" data-testid="login-password-input"
                  type="password" placeholder="Enter your password"
                  value={pwd} onChange={(e) => setPwd(e.target.value)}
                  required
                  className="h-12 pl-10 bg-white border-slate-200 focus-visible:ring-brand-800"
                />
              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3.5 text-sm text-red-700" data-testid="login-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <Button
            type="submit" disabled={busy}
            data-testid="login-submit-button"
            className="w-full h-12 bg-brand-800 hover:bg-brand-700 text-white font-semibold"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

        </form>
      </div>
    </div>
  );
}
